// libs
import React, { Suspense } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CounterPill, Stack, Typography } from '@lyearn/ui';
// components
import * as ContentList from '@/components/ContentList';
import LearnerAndGroupDialog from '@/modules/corporate/components/LearnerAndGroupDialog';
import SeeAllLearnerAndGroup from '@/modules/corporate/components/SeeAllLearnerAndGroup';

import GroupContentList from './components/GroupContentList';
import LearnerContentList from './components/LearnerContentList';
import SeeMore from './components/SeeMore';
import useLearnerAndGroupList from './hooks/useLearnerAndGroupList';
// types
import { LearnersListProps } from './types';

function LearnersAndGroupList(props: LearnersListProps) {
  const { t } = useTranslation();
  const {
    learnerIds,
    userGroupIds,
    disabled,
    canImport,
    hideHeader,
    learnerAndGroupDialog,
    addBlock,
    disabledRemoveIds,
    removedUserIds,
    importedUserGroupIds,
  } = props;
  const {
    learnerListItems,
    onRemoveUser,
    userGroupListItems,
    onRemovegroup,
    showSeeMore,
    toggleSeeAll,
    openLearnerAndGroup,
    toggleLearnerAndGroup,
    onAddLearners,
    openSeeAll,
    totalUserCount,
  } = useLearnerAndGroupList(props);

  return (
    <>
      {!hideHeader ? (
        <Stack alignItems="center" className="mb-20" justifyContent="between">
          <Typography variant="heading-s">{t('AssignmentCreator:LearnersSectionTitle')}</Typography>
          <CounterPill appearance="secondary" label={totalUserCount + userGroupIds.length} />
        </Stack>
      ) : null}
      <LearnerContentList
        disabled={disabled}
        disabledRemoveIds={disabledRemoveIds}
        learnerListItems={learnerListItems}
        onRemoveUser={onRemoveUser}
      />
      {learnerListItems.length && userGroupListItems.length ? (
        <Stack className="mx-8 -mb-1 border-b border-solid border-interactive-border" />
      ) : null}
      <GroupContentList
        disabled={disabled}
        disabledRemoveIds={disabledRemoveIds}
        userGroupListItems={userGroupListItems}
        onRemovegroup={onRemovegroup}
      />
      {showSeeMore ? <SeeMore onClick={toggleSeeAll} /> : null}
      <ContentList.LargeAddBlock
        className="mt-8"
        disabled={disabled}
        subTitle={addBlock?.description ?? t('AssignmentCreator:AddlearnersDescription')}
        title={addBlock?.title ?? t('AssignmentCreator:Addlearners')}
        usage="user"
        onClick={toggleLearnerAndGroup}
      />
      {openSeeAll ? (
        <SeeAllLearnerAndGroup
          disabled={disabled}
          importedUserGroupIds={importedUserGroupIds}
          learnerIds={learnerIds}
          removedUserIds={removedUserIds}
          userGroupIds={userGroupIds}
          onClose={toggleSeeAll}
          onRemoveUser={onRemoveUser}
          onRemovegroup={onRemovegroup}
        />
      ) : null}
      {openLearnerAndGroup ? (
        <Suspense fallback={null}>
          <LearnerAndGroupDialog
            canImport={canImport}
            description={learnerAndGroupDialog?.description}
            disabled={disabled}
            importedUserGroupIds={importedUserGroupIds}
            removedUserIds={removedUserIds}
            selectedLearnerIds={learnerIds}
            selectedUserGroupIds={userGroupIds}
            title={learnerAndGroupDialog?.title}
            onAddLearnerAndGroup={onAddLearners}
            onClose={toggleLearnerAndGroup}
          />
        </Suspense>
      ) : null}
    </>
  );
}

export default React.memo(LearnersAndGroupList);
